/**
 * The allow/deny domain list a browser permission row expands into.
 *
 * @module
 */
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { COARSE_POINTER_HIT_AREA_CLASS, RESPONSIVE_TEXT_BUTTON_CLASS } from "../../lib/touchTarget";
import { useCallback, useState } from "react";

export function DomainList({
  emptyLabel,
  domains,
  onChange,
}: {
  emptyLabel: string;
  domains: ReadonlyArray<string>;
  onChange: (domains: ReadonlyArray<string>) => void;
}) {
  const [draft, setDraft] = useState("");
  const normalizedDraft = draft.trim().toLowerCase();

  const handleAdd = useCallback(() => {
    if (normalizedDraft.length === 0 || domains.includes(normalizedDraft)) return;
    onChange([...domains, normalizedDraft]);
    setDraft("");
  }, [normalizedDraft, domains, onChange]);

  return (
    <div className="mt-4 space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          }}
          placeholder="example.com"
          aria-label="Domain"
        />
        <Button
          className={RESPONSIVE_TEXT_BUTTON_CLASS}
          variant="outline"
          disabled={normalizedDraft.length === 0 || domains.includes(normalizedDraft)}
          onClick={handleAdd}
        >
          Add
        </Button>
      </div>
      {domains.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border/70 py-3 text-center text-sm text-muted-foreground md:text-xs">
          {emptyLabel}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {domains.map((domain) => (
            <span
              key={domain}
              className="inline-flex items-center gap-1.5 rounded-md border border-border/70 bg-muted/40 px-2 py-1 text-sm text-foreground md:text-xs"
            >
              {domain}
              <button
                type="button"
                className={`${COARSE_POINTER_HIT_AREA_CLASS} rounded-sm px-1 text-muted-foreground hover:bg-muted hover:text-foreground`}
                aria-label={`Remove ${domain}`}
                onClick={() => onChange(domains.filter((candidate) => candidate !== domain))}
              >
                x
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
